import { nextEventLoop } from 'helpers/timing';
import ApplicationController from './application_controller';

/**
 * Shows the `Impulse::DialogComponent` when clicked on the button element.
 * Can also auto-open based on URL parameters.
 *
 * @example
 *
 * <button type="button" data-controller="dialog" data-dialog-id-value="dialog-id">
 *   Open dialog
 * </button>
 *
 * For auto-open based on URL params:
 * <button type="button"
 *         data-controller="dialog"
 *         data-dialog-id-value="refund-dialog"
 *         data-dialog-auto-open-value="true">
 *   Open dialog
 * </button>
 *
 * Next, add the dialog markup.
 *
 * <%= render(Impulse::DialogComponent.new(id: "dialog-id", title: "My dialog")) %>
 */
export default class extends ApplicationController {
  static values = {
    id: String,
    autoOpen: Boolean,
    urlParam: String, // Optional: specify which URL param to check
  };

  initialize() {
    this.showDialog = this.showDialog.bind(this);
  }

  connect() {
    this.element.addEventListener('click', this.showDialog);

    // Check for auto-open conditions
    if (this.shouldAutoOpen()) {
      // Small delay to ensure DOM is ready
      nextEventLoop().then(() => {
        this.showDialog();
        this.cleanUrlParams();
      });
    }
  }

  disconnect() {
    this.element.removeEventListener('click', this.showDialog);
  }

  showDialog() {
    const dialog = document.getElementById(this.idValue);
    if (dialog?.open) return;
    dialog?.show();
    this.dispatch('shown', { detail: { dialog, relatedTarget: this.element } });
  }

  shouldAutoOpen() {
    // Check if auto-open is explicitly set via data attribute
    if (this.hasAutoOpenValue && this.autoOpenValue) {
      return true;
    }

    // Check URL parameters for specific dialog triggers
    const urlParams = new URLSearchParams(window.location.search);

    // If a specific URL param is specified, check for it
    if (this.hasUrlParamValue) {
      return urlParams.get(this.urlParamValue) === 'true';
    }

    // Default check for refund dialog
    if (this.idValue === 'refund-dialog' && urlParams.get('refund') === 'true') {
      return true;
    }

    return false;
  }

  cleanUrlParams() {
    const url = new URL(window.location);

    // Remove the relevant parameter based on configuration
    if (this.hasUrlParamValue) {
      url.searchParams.delete(this.urlParamValue);
    } else if (this.idValue === 'refund-dialog') {
      url.searchParams.delete('refund');
    }

    // Update URL without page reload
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
}
